import React, { useEffect, useState } from "react";
import styles from "./ReelsManager.module.css";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress"; // For loader
import { toast } from "react-toastify"; // For toast notifications
import { useAuth } from "../../../contexts/AuthContext";
import { toQueryString } from "../../../util/utilFunctions";
import { smTypes } from "../../../util/flStates";
import Dialog from "@mui/material/Dialog"; // Modal component
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button"; // Button component

function ReelsManager() {
  const { accessToken } = useAuth();
  const [reels, setReels] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [reelData, setReelData] = useState({ type: "Instagram", url: "" });
  const [isLoading, setIsLoading] = useState(false); // Loader state
  const [errorMessage, setErrorMessage] = useState(""); // Error message state
  const [openDialog, setOpenDialog] = useState(false); // Modal state
  const [selectedReelId, setSelectedReelId] = useState(null); // Track selected reel for deletion

  const handleInputChange = (e) => {
    setReelData({ ...reelData, [e.target.name]: e.target.value });
  };

  const addReel = async () => {
    if (!reelData.url.trim()) {
      setErrorMessage("Reel URL cannot be empty.");
      return;
    }

    setIsLoading(true); // Show loader
    setErrorMessage(""); // Reset error message

    //console.log(reelData);

    try {
      const requestData = {
        typeSMId: 2,
        url: reelData.url,
      };

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-sm/add`,
        requestData,
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      if (response.status === 200 && response.data && response.data.success) {
        if (editIndex !== null) {
          const updatedReels = [...reels];
          updatedReels[editIndex] = reelData;
          setReels(updatedReels);
          setEditIndex(null);
          toast.success("Reel updated successfully!");
        } else {
          setReels([...reels, reelData]);
          toast.success("Reel added successfully!");
        }
        setReelData({ type: "Instagram", url: "" });
      } else {
        const errorMsg =
          response.data.message || "Failed to upload the social media link.";
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        "Error uploading the social media link. Please try again!";
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false); // Hide loader
    }
  };

  const editReel = (index) => {
    setEditIndex(index);
    setReelData(reels[index]);
  };

  // Open confirmation modal before deletion
  const handleDeleteClick = (reelId) => {
    setSelectedReelId(reelId);
    setOpenDialog(true);
  };

  // Close modal
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedReelId(null);
  };

  const confirmDelete = async () => {
    try {
      const requestData = {
        id: selectedReelId,
      };

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-sm/delete/`,
        requestData,
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      if (response.status === 200 && response.data && response.data.success) {
        setReels(reels.filter((reel) => reel.id !== selectedReelId));
        toast.success("Reel deleted successfully!");
        handleCloseDialog();
      } else {
        const errorMsg =
          response.data.message || "Failed to delete the social media link.";
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error("Failed to delete the reel. Please try again.");
    }
  };

  const getEmbedUrl = (url) => {
    if (url) {
      return `https://www.instagram.com/p/${url
        .split("/")
        .slice(-2, -1)}/embed/`;
    }
    return null;
  };

  useEffect(() => {
    const fetchReels = async (accessToken) => {
      try {
        const queryString = toQueryString({
          typeSMId: smTypes.instagramReel,
        });

        const response = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/dash/pro-sm/list?${queryString}`,
          {
            headers: {
              Authorization: accessToken,
            },
          }
        );

        if (response.status === 200 && response.data && response.data.success) {
          setReels(response.data.smLinks);
        } else {
          const errorMsg =
            response.data.message || "Failed to fetch the social media links.";
          setErrorMessage(errorMsg);
          toast.error(errorMsg);
        }
      } catch (error) {
        console.error("Error while fetching all the Instagram reels");
      }
    };

    if (accessToken) {
      fetchReels(accessToken);
    }

    return () => {};
  }, [accessToken]);

  return (
    <div className={styles.container}>
      <h3 className={styles.heading}>Manage Instagram Reels</h3>

      <div className={styles.instructions}>
        <h4>How to Add Your Instagram Reels</h4>
        <ul>
          <li>Login to your Instagram account.</li>
          <li>Upload a new reel or select an existing one.</li>
          <li>Right-click on the reel and select "Copy Link."</li>
          <li>Paste the link in the field below and click "Add."</li>
        </ul>
      </div>

      {errorMessage && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}

      <div className={styles.form}>
        <input
          type="text"
          name="url"
          placeholder="Enter Reel URL"
          value={reelData.url}
          onChange={handleInputChange}
          className={styles.input}
        />
        <button
          onClick={addReel}
          className={styles.button}
          disabled={isLoading}
        >
          {isLoading ? "Saving..." : editIndex !== null ? "Update" : "Add"}
        </button>
      </div>

      <div className={styles.reelsGrid}>
        {reels.map((reel, index) => (
          <div key={index} className={styles.reelBox}>
            <iframe
              className={styles.video}
              src={getEmbedUrl(reel.url)}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            <div className={styles.reelInfo}>
              <p>
                <a href={reel.url} target="_blank" rel="noopener noreferrer">
                  {reel.url}
                </a>
              </p>
              <div className={styles.actions}>
                {/* <button
                  onClick={() => editReel(index)}
                  className={styles.editBtn}
                >
                  Edit
                </button> */}
                <button
                  onClick={() => handleDeleteClick(reel.id)}
                  className={styles.deleteBtn}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal for delete confirmation */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="delete-confirmation-dialog"
      >
        <DialogTitle id="delete-confirmation-dialog">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Instagram reel?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="secondary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ReelsManager;
